import React, { useState, useEffect, useRef } from "react"
import _ from "lodash"
import moment from "moment"
import Isvg from "react-inlinesvg"

import propertyService from "data/property"
import validationService from "data/validate"
import snapshotService from "data/snapshots"
import automationService from "data/automation"
import notificationService from "data/notification"
import socketService, * as eventType from "data/socket"
import { humanize, localizeNum } from "data/lib/numbers"
import { copy, paste, canPaste } from "data/lib/copy-paste"

import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"

import DataTable_s from "components/table/data-table-styles"
import SearchHeader from "components/table/search-header"
import DeletePropConfirmation from "components/folder/property-list/delete-prop-confirmation"
import { TableAutomation, focusCellByContent, focusCellByDataId, focusFirstCellByRowIndex, pageToLast } from "components/table/table-automation"
import { DateField, NumberField } from "components/table/display"
import { TextEdit, NumberEdit, DateEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
import { alphanum } from "components/table/lib/sort"
import { field as _field } from "components/table/lib/column-field"
import RowCalcStatus from "components/folder/calc/row-calc-status"

import { CommercialIcon, HospitalityIcon, MixedUseIcon, MFRentIcon } from 'images/icons/sidebar'

const PropertyListTable = (props: {data: object[]}) => {
  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: e => { if(contextRow.systeminput) return;
      propertyService.copyProperty(contextRow.folderid, contextRow.propertyid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        await propertyService.copies(contextRow.folderid, contextRow.propertyid, n)
      })
    }},
    {label: 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      if(contextRow.propertyid === _.get(propertyService.get(), "propertyid")) {
        notificationService.notify({
          type: "danger", message: "You cannot delete the currently selected property", dismiss: 3000
        })
      } else {
        automationService.broadcast("MODAL_OPENING", {})
        deleteConfirmVisible(true)
      }
    }}
  ]

  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<{folderid: number, propertyid: number, code: string, systeminput?: string}>({folderid: 0, propertyid: 0, code: ""})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [showDeleteConfirm, deleteConfirmVisible] = useState<boolean>(false)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const tableId = "property-list-table"
  const field = _field('property')

  useEffect(() => {
    const socketSubscription = socketService.stream().subscribe(socket => {
      switch(true) {
        case socket.eventType === eventType.PROPERTY_COPY_FINISHED_EVENT:
          setTimeout(() => focusCellByContent(tableId, socket.event.property.code), 300)
          break
      }
    })

    const propertyEventSubscription = propertyService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "PROPERTY_CREATED":
          setTimeout(() => pageToLast(tableId), 1)
          setTimeout(() => focusCellByContent(tableId, _.get(e.payload, "code")), 500)
          break
      }
    })

    return function cleanup() {
      socketSubscription.unsubscribe()
      propertyEventSubscription.unsubscribe()
    }
  }, [])

  // !  The order in which sortFn is called by PrimeReact vs the render has closure/caching issues,
  // !  so having this callback use a traditional fn binding gives access to DataTable.props.value
  // !  which is in sync.
  const comparator = (field) => {
    const isValuation = ['lastCalcValue', 'lastCalcValuePerArea'].includes(field)
    const get = (x) => isValuation ? _.get(_.find(_.get(x, 'valuationdetail'), 'defaultScenario'), field) : _.get(x, field)
    return (a,b) => alphanum(get(a), get(b))
  }
  type DataTableComponent = {props:{value: object[]}}
  function sortFn (this: DataTableComponent, event) {
    const data = [...this.props.value]
    const { field, order } = event
    data.sort(comparator(field))
    if (order === 1) data.reverse()
    return data
  }

  const persist = (value, field, rowData) => {
    const afterChange = _.set(_.cloneDeep(rowData), field, value)

    propertyService.patch(afterChange)
      .then(() => validationService.validateAfterChange(rowData, afterChange, propertyService.value()))
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, propertyService.fetchProperty, 'property')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu([]) : setMenu(nonSystemMenu)
  }, [contextCell])

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( tableId, contextRow.propertyid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  const valuationBody = (field, fn) => (rowData) => {
    let value = _.get(_.find(rowData.valuationdetail, 'defaultScenario'), field)
    if (_.isNil(value)) value = '-'
    else value = fn(value)
    return value
  }

  return (
    <DataTable_s id={`${tableId}-scrollable-container`}>
      <DataTable
        id={tableId}
        value={props.data.sort(comparator('code'))}
        paginator={true}
        rows={20}
        rowsPerPageOptions={[10, 20, 50, 100, 500]}
        header={<SearchHeader setSearchFilter={setSearchFilter} />}
        globalFilter={searchFilter}
        scrollable={true}
        paginatorPosition="bottom"
        frozenWidth="300px"
        stateStorage="custom"
        customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
        customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}>
        <Column body={(rowData, props) => props.rowIndex + 1} header="#" frozen={true} style={{width: "50px"}} />
        <Column body={(rowData, props) => <Isvg src={rowData.type === 'Multifamily' ? MFRentIcon : rowData.type === 'Hospitality' ? HospitalityIcon : rowData.type === 'Mixed-Use' ? MixedUseIcon : CommercialIcon} />} header="Type" style={{width: "50px"}} frozen={true} />
        <Column body={(rowData, props) => <RowCalcStatus propertyId={rowData.propertyid}/>} header="Status" style={{width: "60px"}} frozen={true} />
        <Column {...field('code')} header="Code" sortable={true} style={{width: "140px"}} frozen={true}
          editor={props => TextEdit({persist, ...props, keyfilter: /^[^*]*$/})} sortFunction={sortFn}/>
        <Column {...field("name")} header="Name" sortable={true} style={{width: "130px"}}
          editor={props => TextEdit({persist, ...props})} sortFunction={sortFn}/>
        <Column {...field("analysisbegindate", (rowData, props) => DateField({withoutDays: true, ...props}))} header="Analysis Begin" sortable={true} style={{width: "130px"}}
          editor={props => DateEdit({withoutDays: true, persist, ...props})} sortFunction={sortFn}/>
        <Column {...field("reportbegindate", (rowData, props) => DateField({withoutDays: true, ...props}))} header="Report Begin" sortable={true} style={{width: "120px"}}
          editor={props => DateEdit({withoutDays: true, persist, ...props})} sortFunction={sortFn}/>
        <Column {...field("term", (rowData, props) => NumberField({readPrecision: 0, ...props}))} header="Term" sortable={true} style={{width: "80px"}}
          editor={props => NumberEdit({persist, ...props})} sortFunction={sortFn}/>
        <Column {...field("lastCalcValue")} body={valuationBody('lastCalcValue', value => humanize(value, 2, true))} header="Value" sortable={true} style={{width: "100px", textAlign: "right", paddingRight: "8px"}} sortFunction={sortFn}/>
        <Column {...field("lastCalcValuePerArea")} body={valuationBody('lastCalcValuePerArea', value => localizeNum(value, 2, false))} header="Per Area" sortable={true} style={{width: "100px", textAlign: "right", paddingRight: "8px"}} sortFunction={sortFn}/>
        <Column field="activitydetail.lastupdated" header="Last Updated" sortable={true} sortFunction={sortFn} style={{width: "180px"}}
          body={rowData => {
            const rawDate = _.get(rowData, "activitydetail.lastupdated")
            return rawDate ? moment(rawDate).format("MM/DD/YYYY hh:mm:ss a") : null
          }}/>
        <Column field="activitydetail.user" header="Last Updated By" sortable={true} sortFunction={sortFn} style={{width: "180px"}} />
      </DataTable>
      <TableAutomation tableId="property-list-table"/>
     {showDeleteConfirm &&
      <DeletePropConfirmation
        show={showDeleteConfirm}
        code={contextRow.code}
        close={() => {
          deleteConfirmVisible(false)
          setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
        }}
        confirm={() => {
          propertyService.deleteProperty(contextRow.propertyid)
          deleteConfirmVisible(false)
          automationService.broadcast("MODAL_CLOSING", {})
        }}
        snapshotButtonText="Create Snapshot"
        snapshotFunc={() => {
          automationService.broadcast("MODAL_CLOSING", {})
          deleteConfirmVisible(false)
          snapshotService.createSnapshot("Property", null, true, contextRow.propertyid)
          .then(() => {
            propertyService.deleteProperty(contextRow.propertyid)
            notificationService.notify({
              type: "success",
              message: `The property snapshot of ${contextRow.code} was successful.`,
              dismiss: 3000
            })
            setTimeout(() => focusFirstCellByRowIndex(tableId, _.get(contextRow, "index") - 2 >= 0 ? _.get(contextRow, "index") : 0), 400)
          })
        }}/>
      }
      { duplicateConfirmation }
    </DataTable_s>
  )
}

export default PropertyListTable
